import { ArchiveBoxIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { PrimaryButton } from '../../components/buttons';
import { DownloadUploaderButton } from '../../components/download-uploader-button';
import { EditColumns } from '../../components/edit-columns';
import { EmptyState } from '../../components/empty-state';
import { LoadingOverlay } from '../../components/loading';
import { Actions } from '../../components/product-table/columns/actions-column';
import { Barcode } from '../../components/product-table/columns/barcode-column';
import { Bin } from '../../components/product-table/columns/bin-column';
import { Brand } from '../../components/product-table/columns/brand-column';
import { CaseQty } from '../../components/product-table/columns/case-qty-column';
import { Department } from '../../components/product-table/columns/department-column';
import { Description } from '../../components/product-table/columns/description-column';
import { DOH } from '../../components/product-table/columns/doh-column';
import { ItemCode } from '../../components/product-table/columns/item-code-column';
import { LastCost } from '../../components/product-table/columns/last-cost-column';
import { LastReceived } from '../../components/product-table/columns/last-received-column';
import { LastSold } from '../../components/product-table/columns/last-sold-column';
import { MarginDollars } from '../../components/product-table/columns/margin-dollars-column';
import { MarginPercentage } from '../../components/product-table/columns/margin-percentage-column';
import { OrderLot } from '../../components/product-table/columns/order-lot-column';
import { Price } from '../../components/product-table/columns/price-column';
import { ProductNameWithLabels } from '../../components/product-table/columns/product-name-with-labels-column';
import { QOHCases } from '../../components/product-table/columns/qoh-cases-column';
import { QOH } from '../../components/product-table/columns/qoh-column';
import { Reorder } from '../../components/product-table/columns/reorder-column';
import {
  getCasesSoldColumn,
  getUnitsSoldColumn,
} from '../../components/product-table/columns/sales-columns';
import { Size } from '../../components/product-table/columns/size-column';
import { StdQty } from '../../components/product-table/columns/std-qty-column';
import { Type } from '../../components/product-table/columns/type-column';
import { VendorName } from '../../components/product-table/columns/vendor-name-column';
import { DisplayInCasesToggle } from '../../components/product-table/display-in-cases-toggle';
import { DepartmentFilter } from '../../components/product-table/filters/department-filter';
import { FiltersPopover } from '../../components/product-table/filters/filters-popover';
import { HideDisabledFilter } from '../../components/product-table/filters/hide-disabled-filter';
import { LabelFilter } from '../../components/product-table/filters/label-filter';
import { TypeFilter } from '../../components/product-table/filters/type-filter';
import { VendorNameFilter } from '../../components/product-table/filters/vendor-name-filter';
import type { StrictColumnDef } from '../../components/tanstack-table/column';
import { TanStackTableCSVButton } from '../../components/tanstack-table/csv-button';
import type { AppTable } from '../../components/tanstack-table/features';
import { TanStackTable } from '../../components/tanstack-table/table';
import { useRouteRowSelection } from '../../components/tanstack-table/use-route-row-selection';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import { ProductTableContext } from '../../contexts/product-table-context';
import type { Product } from '../../data/product';
import { type Filters, useFilteredProducts, useProducts } from '../../hooks/products';
import { useVisibleColumns } from '../../hooks/use-visible-columns';

const PRODUCT_COLUMNS_KEY = 'product-columns';

// Display columns (what's visible in the table)
const getDisplayColumns = ({
  displayInCases,
}: {
  displayInCases?: boolean;
}): StrictColumnDef<Product>[] => [
  VendorName,
  Bin,
  ItemCode,
  Barcode,
  ProductNameWithLabels,
  Department,
  Type,
  Size,
  CaseQty,
  StdQty,
  OrderLot,
  LastCost,
  Price,
  MarginPercentage,
  MarginDollars,
  displayInCases ? getCasesSoldColumn(7) : getUnitsSoldColumn(7),
  displayInCases ? getCasesSoldColumn(14) : getUnitsSoldColumn(14),
  displayInCases ? getCasesSoldColumn(30) : getUnitsSoldColumn(30),
  displayInCases ? getCasesSoldColumn(60) : getUnitsSoldColumn(60),
  displayInCases ? getCasesSoldColumn(90) : getUnitsSoldColumn(90),
  displayInCases ? getCasesSoldColumn(180) : getUnitsSoldColumn(180),
  displayInCases ? getCasesSoldColumn(365) : getUnitsSoldColumn(365),
  displayInCases ? QOHCases : QOH,
  DOH,
  Reorder,
  LastReceived,
  LastSold,
  Actions,
];

// Hidden columns used only for sorting
const hiddenSortColumns: StrictColumnDef<Product>[] = [Brand, Description];

// The sort-only columns, shaped for the column picker's locked rows.
const sortOnlyColumns = hiddenSortColumns.map((col) => ({ id: col.id, label: col.id }));

// Columns that start hidden for a user who has never touched the picker.
// Also what "Reset columns" restores — keep EditColumns and useVisibleColumns
// reading the same list.
const DEFAULT_HIDDEN_COLUMNS = [Bin.id];

const defaultFilters: Filters = {
  query: '',
  departments: [],
  vendorNames: [],
  types: [],
  sizes: [],
  displayInCases: false,
  hideDisabled: true,
};

// Multi-column sort order
const sorting: SortingState = [
  { id: VendorName.id, desc: false },
  { id: Department.id, desc: false },
  { id: Size.id, desc: true },
  { id: Brand.id, desc: false },
  { id: Description.id, desc: false },
];

// Column visibility: hide the sort-only columns
const hiddenSortColumnVisibility = Object.fromEntries(
  hiddenSortColumns.map((col) => [col.id, false])
);

export function Products() {
  const { saving, products } = useProducts();
  const { filters, setFilters, filteredProducts, baselineCount, clearAll } = useFilteredProducts(
    products,
    defaultFilters
  );
  const { rowSelection, onRowSelectionChange, onRowClick } = useRouteRowSelection();
  const [table, setTable] = useState<AppTable<Product> | null>(null);

  // Get display columns based on displayInCases filter, apply user visibility preferences
  const displayColumns = useMemo(
    () => getDisplayColumns({ displayInCases: filters.displayInCases }),
    [filters.displayInCases]
  );
  const {
    columns: editableColumns,
    columnVisibility: userColumnVisibility,
    columnOrder,
  } = useVisibleColumns(displayColumns, PRODUCT_COLUMNS_KEY, {
    defaultHiddenColumnIds: DEFAULT_HIDDEN_COLUMNS,
  });
  const columns = useMemo(() => [...editableColumns, ...hiddenSortColumns], [editableColumns]);
  const mergedColumnVisibility = useMemo(
    () => ({ ...hiddenSortColumnVisibility, ...userColumnVisibility }),
    [userColumnVisibility]
  );

  if (products.length === 0) {
    return (
      <EmptyState
        icon={<ArchiveBoxIcon className="size-8" />}
        title="No products imported yet."
        description="Install the uploader to start syncing your LiquorPOS catalog."
        action={<DownloadUploaderButton primary />}
        footnote="The uploader runs on the same Windows PC as LiquorPOS. Products appear here automatically once it is syncing."
      />
    );
  }

  return (
    <>
      <ProductTableContext.Provider value={{ filters, setFilters, filteredProducts }}>
        <PageToolbar
          left={
            <>
              <FiltersPopover defaultFilters={defaultFilters}>
                <HideDisabledFilter />
                <VendorNameFilter products={products} />
                <DepartmentFilter products={products} />
                <TypeFilter products={products} />
                <LabelFilter />
              </FiltersPopover>
              <EditColumns
                tableKey={PRODUCT_COLUMNS_KEY}
                defaultHiddenColumnIds={DEFAULT_HIDDEN_COLUMNS}
                sorting={sorting}
                sortOnlyColumns={sortOnlyColumns}
              />
            </>
          }
          right={
            <>
              <DisplayInCasesToggle />
              <SearchWithCount
                quantity={filteredProducts.length}
                total={baselineCount}
                noun="products"
              />
              <TanStackTableCSVButton
                table={table}
                filename={`products-${new Date().toISOString().split('T')[0]}.csv`}
              />
            </>
          }
        />
      </ProductTableContext.Provider>
      <TanStackTable
        data={filteredProducts}
        columns={columns}
        sorting={sorting}
        columnVisibility={mergedColumnVisibility}
        columnOrder={columnOrder}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        onRowClick={onRowClick}
        getRowId={(product) => product.id}
        onTableReady={setTable}
        emptyState={
          <EmptyState
            icon={<FunnelIcon className="size-8" />}
            title="No products match these filters."
            description={
              baselineCount > 0 ? (
                <span className="tabular-nums">
                  0 of{' '}
                  <span className="font-semibold text-gray-700">
                    {baselineCount.toLocaleString()}
                  </span>{' '}
                  products
                </span>
              ) : undefined
            }
            action={
              <PrimaryButton onClick={clearAll}>
                <XMarkIcon className="size-5" />
                Clear all filters
              </PrimaryButton>
            }
          />
        }
      />
      {saving && <LoadingOverlay />}
    </>
  );
}
